import { useCallback, useEffect, useRef, useState } from 'react';

export type SoundSettings = {
  /** Gain multiplier applied on top of the player volume. 1 = unchanged, 10 = 1000%. */
  boost: number;
  /** Low shelf in dB (-12..12). */
  bass: number;
  /** High shelf in dB (-12..12). */
  treble: number;
  /** Compressor that evens out quiet and loud parts and prevents clipping when boosting. */
  normalize: boolean;
};

export const DEFAULT_SOUND: SoundSettings = { boost: 1, bass: 0, treble: 0, normalize: false };

export const SOUND_PRESETS: { id: string; label: string; values: SoundSettings }[] = [
  { id: 'normal', label: 'Normal', values: DEFAULT_SOUND },
  { id: 'loud', label: 'Loud', values: { boost: 2.5, bass: 2, treble: 2, normalize: true } },
  { id: 'max', label: 'Max', values: { boost: 8, bass: 3, treble: 3, normalize: true } },
  { id: 'bass', label: 'Bass boost', values: { boost: 2, bass: 8, treble: 0, normalize: true } },
  { id: 'voice', label: 'Clear voice', values: { boost: 2.5, bass: -3, treble: 5, normalize: true } },
];

const STORAGE_KEY = 'qs-sound-settings';

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const sanitize = (raw: Partial<SoundSettings> | null | undefined): SoundSettings => ({
  boost: clamp(Number(raw?.boost ?? 1) || 1, 1, 10),
  bass: clamp(Number(raw?.bass ?? 0) || 0, -12, 12),
  treble: clamp(Number(raw?.treble ?? 0) || 0, -12, 12),
  normalize: !!raw?.normalize,
});

const loadSettings = (): SoundSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? sanitize(JSON.parse(raw)) : DEFAULT_SOUND;
  } catch {
    return DEFAULT_SOUND;
  }
};

type Graph = {
  ctx: AudioContext;
  gain: GainNode;
  bass: BiquadFilterNode;
  treble: BiquadFilterNode;
  comp: DynamicsCompressorNode;
};

// A media element can only be wired into Web Audio once, so the graph lives for the element's lifetime.
const graphs = new WeakMap<HTMLMediaElement, Graph>();

const buildGraph = (el: HTMLMediaElement): Graph | null => {
  const existing = graphs.get(el);
  if (existing) return existing;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    const ctx = new Ctor();
    const source = ctx.createMediaElementSource(el);
    const bass = ctx.createBiquadFilter();
    bass.type = 'lowshelf';
    bass.frequency.value = 200;
    const treble = ctx.createBiquadFilter();
    treble.type = 'highshelf';
    treble.frequency.value = 3500;
    const gain = ctx.createGain();
    const comp = ctx.createDynamicsCompressor();
    source.connect(bass);
    bass.connect(treble);
    treble.connect(gain);
    gain.connect(comp);
    comp.connect(ctx.destination);
    const graph = { ctx, gain, bass, treble, comp };
    graphs.set(el, graph);
    return graph;
  } catch {
    return null;
  }
};

const applySettings = (g: Graph, s: SoundSettings) => {
  const t = g.ctx.currentTime;
  g.gain.gain.setTargetAtTime(s.boost, t, 0.02);
  g.bass.gain.setTargetAtTime(s.bass, t, 0.02);
  g.treble.gain.setTargetAtTime(s.treble, t, 0.02);
  // Always keep a limiter in the chain; "normalize" makes it work harder to even out the levels.
  g.comp.threshold.setTargetAtTime(s.normalize ? -36 : -3, t, 0.02);
  g.comp.knee.setTargetAtTime(s.normalize ? 24 : 0, t, 0.02);
  g.comp.ratio.setTargetAtTime(s.normalize ? 8 : 20, t, 0.02);
  g.comp.attack.setTargetAtTime(s.normalize ? 0.01 : 0.003, t, 0.02);
  g.comp.release.setTargetAtTime(s.normalize ? 0.25 : 0.1, t, 0.02);
};

/**
 * Adds volume boost / bass / treble / normalizer to an <audio> or <video> element through Web Audio.
 * The element must be rendered with crossOrigin="anonymous"; if the media host sends no CORS headers
 * the element is reloaded without it and the sound settings are reported as unsupported (plain playback).
 */
export function useSoundBoost(mediaRef: React.RefObject<HTMLMediaElement | null>) {
  const [settings, setSettings] = useState<SoundSettings>(loadSettings);
  const [supported, setSupported] = useState(true);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const detachRef = useRef<(() => void) | null>(null);
  const attachedRef = useRef<HTMLMediaElement | null>(null);

  // Runs after every render: players such as the video dialog mount their element later, so the ref
  // is checked each time and listeners move to the new element when it changes.
  useEffect(() => {
    const el = mediaRef.current;
    if (el === attachedRef.current) return;
    detachRef.current?.();
    detachRef.current = null;
    attachedRef.current = el;
    if (!el) return;
    setSupported(true);

    const attach = () => {
      if (!el.crossOrigin) return; // CORS fallback already applied, leave it alone
      const g = buildGraph(el);
      if (!g) return;
      applySettings(g, settingsRef.current);
      if (g.ctx.state === 'suspended') g.ctx.resume().catch(() => {});
    };

    const onError = () => {
      // Without CORS headers the browser refuses to load the media at all with crossOrigin set.
      if (el.crossOrigin && !graphs.has(el)) {
        const t = el.currentTime;
        el.removeAttribute('crossorigin');
        el.load();
        el.currentTime = t;
        setSupported(false);
      }
    };

    el.addEventListener('play', attach);
    el.addEventListener('error', onError);
    if (!el.paused) attach();
    detachRef.current = () => {
      el.removeEventListener('play', attach);
      el.removeEventListener('error', onError);
    };
  });

  useEffect(() => () => detachRef.current?.(), []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* storage unavailable */
    }
    const el = mediaRef.current;
    const g = el ? graphs.get(el) : undefined;
    if (g) applySettings(g, settings);
  }, [settings, mediaRef]);

  const update = useCallback((patch: Partial<SoundSettings>) => {
    setSettings((prev) => sanitize({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => setSettings(DEFAULT_SOUND), []);

  return { settings, update, reset, supported };
}
